import axios from "axios";
import { getData } from "../../../backend/api";
import { normalizeLocation } from "./tariffs";

/*
 * Schnittstelle zum Hauptbackend (REACT_APP_API).
 *
 * GET  search-parking?lat&lng&radius&activeTab&parkingFrom[&parkingUntil|duration]
 *        -> [ { parkingId, code?, title, address, lat, lng, imageUrl|images[], spaces?, freeSpaces?, maxHeight?, openingHours?, note?,
 *               tariffs?: [ { id, type: hour|day|night|nightsub|workday|full, name?, price, maxPerDay?, start?, end?, weekdays?, free? } ],
 *               price? (alte API) } ]
 * GET  parking/:id                     -> ein Standort (gleiches Format)
 * GET  parking-session?licensePlate[&parkingId]
 *        -> { sessionId, parkingId, entryTime, options: [ { tariffId, type, label?, amount, start?, end? } ] }   offener Parkvorgang (404 = keiner)
 * POST payment/checkout  (JSON)         -> { url }   Stripe-Checkout (bei Vor-Ort-Zahlung zusätzlich sessionId)
 * POST bookings/access-link (JSON)      -> 200       Zugangslink per E-Mail
 * GET  bookings?token=                  -> [ Buchungen ]
 * POST bookings/:id/cancel (JSON)       -> 200       Abo kündigen
 */

const base = () => process.env.REACT_APP_API || "";

const unwrap = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.parkings)) return data.parkings;
  return [];
};

export const searchLocations = async (params) => {
  const res = await getData(params, "search-parking");
  if (!res || res.status !== 200) throw new Error("search failed");
  return unwrap(res.data)
    .map((raw) => normalizeLocation(raw, params))
    .filter(Boolean);
};

export const getLocation = async (id, ctx = {}) => {
  const res = await getData({}, `parking/${encodeURIComponent(id)}`);
  if (!res || res.status !== 200 || !res.data) throw new Error("not found");
  const raw = res.data.data && !Array.isArray(res.data.data) ? res.data.data : res.data;
  const loc = normalizeLocation(raw, ctx);
  if (!loc) throw new Error("not found");
  return loc;
};

// Offenen Parkvorgang zu einem Kennzeichen suchen (parkingId optional = alle Parkplätze). null = keine Einfahrt erfasst
export const findSession = async (parkingId, licensePlate) => {
  try {
    const params = parkingId ? { parkingId, licensePlate } : { licensePlate };
    const res = await axios.get(`${base()}parking-session`, { params });
    const d = res.data && res.data.data ? res.data.data : res.data;
    if (!d || !d.sessionId) return null;
    return {
      sessionId: String(d.sessionId),
      parkingId: d.parkingId !== undefined && d.parkingId !== null ? String(d.parkingId) : null,
      entryTime: d.entryTime || null,
      options: (Array.isArray(d.options) ? d.options : [])
        .filter((o) => o && o.amount !== undefined)
        .map((o) => ({ tariffId: String(o.tariffId ?? o.type), type: o.type, label: o.label || null, amount: Number(o.amount), start: o.start || null, end: o.end || null })),
    };
  } catch (err) {
    if (err.response && err.response.status === 404) return null;
    throw err;
  }
};

const postJson = async (url, body) => {
  const res = await axios.post(base() + url, body, { headers: { "Content-Type": "application/json" } });
  return res;
};

export const createCheckout = async (body) => {
  const res = await postJson("payment/checkout", body);
  if (res.status === 200 && res.data && res.data.url) return res.data.url;
  throw new Error("checkout failed");
};

export const requestAccessLink = async (body) => {
  const res = await postJson("bookings/access-link", body);
  if (res.status < 200 || res.status >= 300) throw new Error("failed");
};

export const getBookings = async (token) => {
  const res = await axios.get(`${base()}bookings`, { params: { token } });
  return unwrap(res.data);
};

export const cancelBooking = async (id, token) => {
  const res = await postJson(`bookings/${encodeURIComponent(id)}/cancel`, { token });
  if (res.status < 200 || res.status >= 300) throw new Error("failed");
};

// Letzte Buchung für die Bestätigungsseite merken (Stripe leitet zurück auf /success)
const KEY = "rp_last_booking";
export const rememberBooking = (b) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(b));
  } catch (e) {
    /* privater Modus – Bestätigung zeigt dann nur die Kurzfassung */
  }
};
export const lastBooking = () => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || "null");
  } catch (e) {
    return null;
  }
};
